import { useCallback, useMemo } from "react";
import useGetReportOrganizationProfitability from "hooks/organization/reports/types/useGetReportOrganizationProfitability";
import usePaginatedResources from "hooks/usePaginatedResources";

const formatCurrency = (value?: number | null) =>
  value == null ? "N/A" : `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatPercent = (value?: number | null) => (value == null ? "N/A" : `${value.toFixed(2)}%`);

export default function useReportTableOrganizationProfitability(
  startDate,
  endDate,
  productTypeId = ""
) {
  const [getReportOrganizationProfitability, { data, loading, error }] =
    useGetReportOrganizationProfitability(false);

  const paginate = useCallback(
    ({ page = 1, first = 25 } = {}) => {
      getReportOrganizationProfitability({
        variables: {
          startDate,
          endDate,
          productTypeId: productTypeId || null,
          page,
          first,
        },
      });
    },
    [startDate, endDate, productTypeId, getReportOrganizationProfitability]
  );

  const pagination = usePaginatedResources({
    paginate,
    paginatorInfo: data?.getReportOrganizationProfitability?.paginatorInfo,
  });

  const columns = useMemo(
    () => [
      { Header: "Product Type", accessor: "organization_product_type_name", disableSortBy: true },
      { Header: "Proposed Material Cost", accessor: "proposed_cost_material", disableSortBy: true },
      { Header: "Actual Material Cost", accessor: "actual_cost_material", disableSortBy: true },
      { Header: "Material Variance %", accessor: "material_variance_percentage", disableSortBy: true },
      { Header: "Proposed Labor Cost", accessor: "proposed_cost_labour", disableSortBy: true },
      { Header: "Actual Labor Cost", accessor: "actual_cost_labour", disableSortBy: true },
      { Header: "Labor Variance %", accessor: "labour_variance_percentage", disableSortBy: true },
      { Header: "Proposed Equipment Cost", accessor: "proposed_cost_equipment", disableSortBy: true },
      { Header: "Actual Equipment Cost", accessor: "actual_cost_equipment", disableSortBy: true },
      { Header: "Equipment Variance %", accessor: "equipment_variance_percentage", disableSortBy: true },
      { Header: "Proposed Trucking Cost", accessor: "proposed_cost_transfer_truck", disableSortBy: true },
      { Header: "Actual Trucking Cost", accessor: "actual_cost_transfer_truck", disableSortBy: true },
      { Header: "Trucking Variance %", accessor: "trucking_variance_percentage", disableSortBy: true },
      { Header: "Proposed Additional Cost", accessor: "proposed_cost_additional", disableSortBy: true },
      { Header: "Actual Additional Cost", accessor: "actual_cost_additional", disableSortBy: true },
      { Header: "Additional Variance %", accessor: "additional_variance_percentage", disableSortBy: true },
      { Header: "Proposed Profit", accessor: "proposed_profit", disableSortBy: true },
      { Header: "Actual Profit", accessor: "actual_profit", disableSortBy: true },
      { Header: "Profit Variance $", accessor: "profit_variance_dollars", disableSortBy: true },
      { Header: "Profit Variance %", accessor: "profit_variance_percentage", disableSortBy: true },
    ],
    []
  );

  const rows = useMemo(() => {
    const dataRows =
      data?.getReportOrganizationProfitability?.data?.map((row) => ({
        ...row,
        proposed_cost_material: formatCurrency(row.proposed_cost_material),
        actual_cost_material: formatCurrency(row.actual_cost_material),
        material_variance_percentage: formatPercent(row.material_variance_percentage),
        proposed_cost_labour: formatCurrency(row.proposed_cost_labour),
        actual_cost_labour: formatCurrency(row.actual_cost_labour),
        labour_variance_percentage: formatPercent(row.labour_variance_percentage),
        proposed_cost_equipment: formatCurrency(row.proposed_cost_equipment),
        actual_cost_equipment: formatCurrency(row.actual_cost_equipment),
        equipment_variance_percentage: formatPercent(row.equipment_variance_percentage),
        proposed_cost_transfer_truck: formatCurrency(row.proposed_cost_transfer_truck),
        actual_cost_transfer_truck: formatCurrency(row.actual_cost_transfer_truck),
        trucking_variance_percentage: formatPercent(row.trucking_variance_percentage),
        proposed_cost_additional: formatCurrency(row.proposed_cost_additional),
        actual_cost_additional: formatCurrency(row.actual_cost_additional),
        additional_variance_percentage: formatPercent(row.additional_variance_percentage),
        proposed_profit: formatCurrency(row.proposed_profit),
        actual_profit: formatCurrency(row.actual_profit),
        profit_variance_dollars: formatCurrency(row.profit_variance_dollars),
        profit_variance_percentage: formatPercent(row.profit_variance_percentage),
      })) || [];

    const totalRow = data?.getReportOrganizationProfitability?.metadata?.totals;
    if (!totalRow) {
      return dataRows;
    }

    return [
      ...dataRows,
      {
        ...totalRow,
        organization_product_type_name: "Total",
        proposed_cost_material: formatCurrency(totalRow.proposed_cost_material),
        actual_cost_material: formatCurrency(totalRow.actual_cost_material),
        material_variance_percentage: formatPercent(totalRow.material_variance_percentage),
        proposed_cost_labour: formatCurrency(totalRow.proposed_cost_labour),
        actual_cost_labour: formatCurrency(totalRow.actual_cost_labour),
        labour_variance_percentage: formatPercent(totalRow.labour_variance_percentage),
        proposed_cost_equipment: formatCurrency(totalRow.proposed_cost_equipment),
        actual_cost_equipment: formatCurrency(totalRow.actual_cost_equipment),
        equipment_variance_percentage: formatPercent(totalRow.equipment_variance_percentage),
        proposed_cost_transfer_truck: formatCurrency(totalRow.proposed_cost_transfer_truck),
        actual_cost_transfer_truck: formatCurrency(totalRow.actual_cost_transfer_truck),
        trucking_variance_percentage: formatPercent(totalRow.trucking_variance_percentage),
        proposed_cost_additional: formatCurrency(totalRow.proposed_cost_additional),
        actual_cost_additional: formatCurrency(totalRow.actual_cost_additional),
        additional_variance_percentage: formatPercent(totalRow.additional_variance_percentage),
        proposed_profit: formatCurrency(totalRow.proposed_profit),
        actual_profit: formatCurrency(totalRow.actual_profit),
        profit_variance_dollars: formatCurrency(totalRow.profit_variance_dollars),
        profit_variance_percentage: formatPercent(totalRow.profit_variance_percentage),
        style: {
          fontWeight: "bold",
          backgroundColor: "#f5f5f5",
          borderTop: "2px solid #ddd",
        },
      },
    ];
  }, [data]);

  const tableData = useMemo(() => ({ columns, rows }), [columns, rows]);

  return {
    columns,
    data,
    loading,
    error,
    tableData,
    pagination,
    getReportOrganizationProfitability: paginate,
    metadata: data?.getReportOrganizationProfitability?.metadata,
  } as const;
}
